import React from "react";

import style from "./styles.module.scss";

const INLINE_TOKEN = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|\[[^\]\n]+\]\([^)\s]+\))/g;
const BOLD = /^\*\*([^*\n]+)\*\*$/;
const ITALIC = /^\*([^*\n]+)\*$/;
const LINK = /^\[([^\]\n]+)\]\(([^)\s]+)\)$/;
const SAFE_URL = /^https?:\/\//i;
const LIST_ITEM = /^\s*-\s+/;

interface Group {
  list: boolean;
  lines: string[];
}

function renderInline(text: string): React.ReactNode[] {
  return text.split(INLINE_TOKEN).filter(Boolean).map((part, index) => {
    const bold = part.match(BOLD);
    if (bold) {
      return <strong key={index}>{bold[1]}</strong>;
    }

    const italic = part.match(ITALIC);
    if (italic) {
      return <em key={index}>{italic[1]}</em>;
    }

    const [, label, url] = part.match(LINK) || [];
    if (label && url && SAFE_URL.test(url)) {
      return (
        <a key={index} className={style.link} href={url} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
      );
    }

    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

function groupLines(block: string): Group[] {
  return block.split("\n").reduce<Group[]>((groups, line) => {
    const list = LIST_ITEM.test(line);
    const last = groups[groups.length - 1];
    const content = list ? line.replace(LIST_ITEM, "") : line;

    if (last && last.list === list) {
      last.lines.push(content);
    } else {
      groups.push({ list, lines: [content] });
    }

    return groups;
  }, []);
}

interface Props {
  text: string;
}

export default function NoticeText({ text }: Props) {
  const blocks = text.replace(/\r\n/g, "\n").split(/\n{2,}/).map((block) => block.trim()).filter(Boolean);

  return (
    <div className={style.text}>
      {blocks.map((block, blockIndex) => groupLines(block).map((group, groupIndex) => {
        const key = `${blockIndex}-${groupIndex}`;

        if (group.list) {
          return (
            <ul key={key} className={`pgm-body-large ${style.list}`}>
              {group.lines.map((line, lineIndex) => <li key={lineIndex}>{renderInline(line)}</li>)}
            </ul>
          );
        }

        return (
          <p key={key} className={`pgm-body-large ${style.paragraph}`}>
            {group.lines.map((line, lineIndex) => (
              <React.Fragment key={lineIndex}>
                {lineIndex > 0 && <br />}
                {renderInline(line)}
              </React.Fragment>
            ))}
          </p>
        );
      }))}
    </div>
  );
}
